/**
 * Source-context builder for the rejection / response modal (Task 4074b).
 *
 * Derives the read-only "what am I responding to" block shown above the
 * feedback area in {@link RejectDialog} from the approval card's sections.
 *
 * Two source shapes are supported, mirroring the card renderers:
 *
 *   - `original_review` (renderer `type_aware`): the App Store review the agent
 *     drafted a public reply to. `data = { reviewer?, rating?, territory?,
 *     review_text }` — see `buildSyntheticDetail.ts`. Body is the review text;
 *     fields surface the rating, reviewer, and (when 4074c has landed) the
 *     store territory.
 *
 *   - `original_email` (renderer `original_email`): the inbound customer email a
 *     `send_email` reply is answering. `data = OriginalEmailData { from,
 *     from_name, subject, html_body, plain_text_body, ... }`. Body is the
 *     plain-text body (HTML stripped to text when only an HTML body is present);
 *     fields surface From and Subject.
 *
 * The `original_email` *interaction_email* variant carries only an
 * `interaction_id` (the body is hydrated asynchronously at render time), so no
 * body is available synchronously here — we return `undefined` and let the card
 * behind the modal continue to show the hydrated email. Fabricating an empty
 * block would be worse than omitting it (graceful absence).
 *
 * `body` is always PLAIN TEXT so it can flow through the card-level translate
 * control; HTML is never returned.
 */

import type { ApprovalCardSection, SourceContext, SourceContextField } from '../../types'

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function asNonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null
}

/**
 * Best-effort HTML → plain text. Uses the DOM (this runs in the Tauri webview),
 * dropping non-content nodes (style/script) before reading text so email
 * template CSS never leaks into the block or the translate request. Falls back
 * to a crude tag strip when DOM parsing is unavailable. Mirrors the renderer's
 * own `htmlToPlainText` so the modal body matches the card body.
 */
function htmlToPlainText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc
      .querySelectorAll('style, script, head, noscript, template')
      .forEach((el) => el.remove())
    const text = doc.body?.textContent ?? ''
    return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  } catch {
    return html
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
}

/**
 * Build the review source context from an `original_review` section's data.
 * Returns undefined when there is no usable review text.
 */
function buildReviewContext(
  section: ApprovalCardSection,
): SourceContext | undefined {
  const data = section.data
  if (!isPlainObject(data)) return undefined

  const body = asNonEmptyString(data.review_text)
  if (!body) return undefined

  const fields: SourceContextField[] = []
  const rating = asNonEmptyString(data.rating)
  if (rating) fields.push({ label: 'Rating', value: rating })
  const reviewer = asNonEmptyString(data.reviewer)
  if (reviewer) fields.push({ label: 'Reviewer', value: reviewer })
  // Territory is exposed by sibling task 4074c. Guard for its absence so this
  // task lands cleanly before 4074c; render it only when present.
  const territory = asNonEmptyString(data.territory)
  if (territory) fields.push({ label: 'Store', value: territory })

  return {
    label: section.label || 'Original Review',
    body,
    fields: fields.length > 0 ? fields : undefined,
  }
}

/**
 * Build the email source context from an inline `original_email` section's data
 * (the `original_email` renderer variant). Returns undefined for the
 * `interaction_email` variant (only an `interaction_id` is present — no
 * synchronous body) or when no usable body exists.
 */
function buildEmailContext(
  section: ApprovalCardSection,
): SourceContext | undefined {
  const data = section.data
  if (!isPlainObject(data)) return undefined

  const plain = asNonEmptyString(data.plain_text_body)
  const html = asNonEmptyString(data.html_body)
  const body = plain ?? (html ? htmlToPlainText(html) : null)
  if (!body) return undefined

  const fields: SourceContextField[] = []
  const from = asNonEmptyString(data.from)
  const fromName = asNonEmptyString(data.from_name)
  const senderLine = fromName
    ? from
      ? `${fromName} <${from}>`
      : fromName
    : from
  if (senderLine) fields.push({ label: 'From', value: senderLine })
  const subject = asNonEmptyString(data.subject)
  if (subject) fields.push({ label: 'Subject', value: subject })

  return {
    label: section.label || 'Original Email',
    body,
    fields: fields.length > 0 ? fields : undefined,
  }
}

/**
 * Derive the modal source context from a card's sections. Prefers a review
 * source, then an inline email source. Returns undefined when neither yields a
 * usable body — the dialog then renders as a plain feedback composer (no
 * regression for tools without a source artifact).
 */
export function buildSourceContext(
  sections: ReadonlyArray<ApprovalCardSection> | undefined | null,
): SourceContext | undefined {
  if (!sections || sections.length === 0) return undefined

  const review = sections.find((s) => s.id === 'original_review' && !s.error)
  if (review) {
    const ctx = buildReviewContext(review)
    if (ctx) return ctx
  }

  const email = sections.find((s) => s.id === 'original_email' && !s.error)
  if (email) {
    const ctx = buildEmailContext(email)
    if (ctx) return ctx
  }

  return undefined
}

/**
 * Build the proposed-response context from a `post_review_reply` proposed
 * response section. The card stores `data = { response_text }`.
 */
function buildProposedReviewResponse(
  section: ApprovalCardSection,
): SourceContext | undefined {
  const data = section.data
  if (!isPlainObject(data)) return undefined

  const body = asNonEmptyString(data.response_text)
  if (!body) return undefined

  return {
    label: section.label || 'Proposed Response',
    body,
  }
}

/**
 * Build the proposed-response context from a `send_email` proposed resolution
 * section. The payload shape varies (automation- vs agent-sourced), so we look
 * for the body under the common keys and surface the subject/recipient as
 * header fields when present. HTML bodies are stripped to plain text so the
 * block stays readable and can flow through the translate control.
 */
function buildProposedEmailResponse(
  section: ApprovalCardSection,
): SourceContext | undefined {
  const data = section.data
  if (!isPlainObject(data)) return undefined

  const plain =
    asNonEmptyString(data.body_text) ??
    asNonEmptyString(data.plain_text_body) ??
    asNonEmptyString(data.body)
  const html = asNonEmptyString(data.html_body) ?? asNonEmptyString(data.body_html)
  const body = plain ?? (html ? htmlToPlainText(html) : null)
  if (!body) return undefined

  const fields: SourceContextField[] = []
  const recipient =
    asNonEmptyString(data.recipient) ??
    asNonEmptyString(data.to) ??
    asNonEmptyString(data.to_email)
  if (recipient) fields.push({ label: 'To', value: recipient })
  const subject = asNonEmptyString(data.subject)
  if (subject) fields.push({ label: 'Subject', value: subject })

  return {
    label: section.label || 'Proposed Response',
    body,
    fields: fields.length > 0 ? fields : undefined,
  }
}

/**
 * Derive the proposed-response context (what the agent drafted and is asking the
 * operator to approve) from a card's sections, so the operator can see the
 * proposed reply alongside the original item when rejecting. Mirrors
 * {@link buildSourceContext}: review reply first, then email reply. Returns
 * undefined when neither yields a usable body.
 */
export function buildProposedResponse(
  sections: ReadonlyArray<ApprovalCardSection> | undefined | null,
): SourceContext | undefined {
  if (!sections || sections.length === 0) return undefined

  const reviewReply = sections.find((s) => s.id === 'proposed_response' && !s.error)
  if (reviewReply) {
    const ctx = buildProposedReviewResponse(reviewReply)
    if (ctx) return ctx
  }

  const emailReply = sections.find((s) => s.id === 'proposed_resolution' && !s.error)
  if (emailReply) {
    const ctx = buildProposedEmailResponse(emailReply)
    if (ctx) return ctx
  }

  return undefined
}
